'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Role } from '@/types';
import { SessionUser } from '@/lib/session';
import { AlertTriangle } from 'lucide-react';
import Portal from '@/components/Portal';
import { applyDocumentLocale, DEFAULT_LOCALE, Locale, translate, translateError } from '@/lib/i18n';

type ToastType = 'success' | 'error' | 'info';
export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ConfirmState {
  isOpen: boolean;
  message: string;
  title: string;
  onConfirm: () => void;
}

interface AppContextValue {
  role: Role;
  user: SessionUser | null;
  isAuthenticated: boolean;
  toast: (message: string, type?: ToastType) => void;
  toasts: Toast[];
  dismissToast: (id: string) => void;
  confirm: (message: string, onConfirm: () => void, title?: string) => void;
  themeAccent: string;
  setThemeAccent: (accent: string) => void;
  layoutMode: string;
  setLayoutMode: (mode: string) => void;
  theme: string;
  setTheme: (theme: string) => void;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  te: (message: string) => string;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({
  children,
  initialUser,
}: {
  children: React.ReactNode;
  initialUser: SessionUser | null;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    isOpen: false,
    message: '',
    title: '',
    onConfirm: () => {},
  });
  const counter = useRef(0);

  // Theme states. layoutMode: 'sidebar' (menu on the left) | 'topbar' (menu on top).
  // theme: 'dark' (night, default) | 'light'.
  const [themeAccent, setThemeAccentState] = useState<string>('neon-rose');
  const [layoutMode, setLayoutModeState] = useState<string>('sidebar');
  const [theme, setThemeState] = useState<string>('dark');
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);

  const role: Role = initialUser?.role ?? 'admin';

  // Layout & theme preferences are remembered per-user (keyed by Discord ID) on this device.
  const layoutKey = `xg_layout_${initialUser?.discordId || 'guest'}`;
  const themeKey = `xg_theme_mode_${initialUser?.discordId || 'guest'}`;
  const localeKey = `xg_locale_${initialUser?.discordId || 'guest'}`;

  const t = useCallback((key: string) => translate(locale, key), [locale]);
  const te = useCallback((message: string) => translateError(locale, message), [locale]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = `toast-${counter.current++}`;
      setToasts((prev) => [...prev, { id, message, type }]);
      setTimeout(() => dismissToast(id), 3600);
    },
    [dismissToast]
  );

  const confirm = useCallback((message: string, onConfirm: () => void, title?: string) => {
    setConfirmState({
      isOpen: true,
      message,
      title: title ?? '',
      onConfirm: () => {
        onConfirm();
        setConfirmState((prev) => ({ ...prev, isOpen: false }));
      },
    });
  }, []);

  // Apply the light/night theme: flips the root data-theme attribute (CSS does
  // the heavy lifting) and the monochrome brand accent so it stays readable.
  const applyTheme = useCallback((mode: string) => {
    const root = document.documentElement;
    root.removeAttribute('data-accent');
    root.style.removeProperty('--bg-base');
    root.style.removeProperty('--ambient-bg');
    root.style.removeProperty('--color-brand');
    root.setAttribute('data-theme', mode === 'light' ? 'light' : 'dark');
    if (mode === 'light') {
      root.style.setProperty('--brand', '#18181b');
      root.style.setProperty('--brand-glow', 'rgba(0, 0, 0, 0.15)');
    } else {
      root.style.setProperty('--brand', '#f2f2f2');
      root.style.setProperty('--brand-glow', 'rgba(255, 255, 255, 0.22)');
    }
  }, []);

  const setThemeAccent = useCallback((accent: string) => {
    setThemeAccentState(accent);
    localStorage.setItem('xg_theme_accent', accent);
  }, []);

  const setLayoutMode = useCallback((mode: string) => {
    setLayoutModeState(mode);
    localStorage.setItem(layoutKey, mode);
  }, [layoutKey]);

  const setTheme = useCallback((mode: string) => {
    setThemeState(mode);
    localStorage.setItem(themeKey, mode);
    applyTheme(mode);
  }, [themeKey, applyTheme]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem(localeKey, next);
    applyDocumentLocale(next);
  }, [localeKey]);

  // Load on mount (layout & theme are per-user; theme accent is stored but not applied to CSS)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedAccent = localStorage.getItem('xg_theme_accent') || 'neon-rose';
      const savedLayout = localStorage.getItem(layoutKey) || 'sidebar';
      const savedTheme = localStorage.getItem(themeKey) || 'dark';
      const savedLocale = localStorage.getItem(localeKey);
      const nextLocale: Locale = savedLocale === 'en' ? 'en' : DEFAULT_LOCALE;
      setThemeAccentState(savedAccent);
      setLayoutModeState(savedLayout);
      setThemeState(savedTheme);
      setLocaleState(nextLocale);
      applyTheme(savedTheme);
      applyDocumentLocale(nextLocale);
    }
  }, [applyTheme, layoutKey, themeKey, localeKey]);

  return (
    <AppContext.Provider
      value={{
        role,
        user: initialUser,
        isAuthenticated: !!initialUser,
        toast,
        toasts,
        dismissToast,
        confirm,
        themeAccent,
        setThemeAccent,
        layoutMode,
        setLayoutMode,
        theme,
        setTheme,
        locale,
        setLocale,
        t,
        te,
      }}
    >
      {children}
      {confirmState.isOpen && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
            <div 
              className="fixed inset-0" 
              onClick={() => setConfirmState((prev) => ({ ...prev, isOpen: false }))} 
            />
            <div className="glass-panel w-full max-w-sm rounded-2xl p-6 relative my-auto z-10 border border-white/10 animate-slide-up shadow-2xl text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[#fbbf24]/10 border border-[#fbbf24]/25 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 ic-yellow animate-pulse-glow" />
              </div>
              <h3 className="text-base font-bold text-white mb-2">
                {confirmState.title || t('common.confirmDefault')}
              </h3>
              <p className="text-xs text-[#8a8a8a] mb-6 leading-relaxed whitespace-pre-wrap">{confirmState.message}</p>
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => setConfirmState((prev) => ({ ...prev, isOpen: false }))}
                  className="btn-ghost px-5 py-2 text-xs"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={confirmState.onConfirm}
                  className="btn-primary px-5 py-2 text-xs"
                >
                  {t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
